'use client'

import React from 'react'
import { SectionHeader } from '@/components/ui/section-header'
import Link from 'next/link'
import { SiteHeader } from '@/components/layout/site-header'
import SiteFooter from '@/components/layout/site-footer'
import { Skeleton } from '@/components/ui/skeleton'
import { useLegalDocument } from '@/hooks/use-legal'

export default function PrivacyPage() {
  const { data: legalData, isLoading, error } = useLegalDocument('privacy_policy')
  const document = legalData?.data

  return (
    <div className='min-h-screen bg-gradient-soft'>
      <SiteHeader />
      <main className='container mx-auto px-4 py-12'>
        <SectionHeader
          eyebrow='Legal'
          title='Privacy Policy'
          align='left'
          size='lg'
          className='mb-8'
        />

        {isLoading ? (
          <div className='max-w-5xl space-y-4'>
            <Skeleton className='h-6 w-48' />
            <Skeleton className='h-4 w-full' />
            <Skeleton className='h-4 w-full' />
            <Skeleton className='h-4 w-3/4' />
            <div className='mt-8 space-y-4'>
              {[...Array(5)].map((_, i) => (
                <div key={i} className='space-y-2'>
                  <Skeleton className='h-5 w-40' />
                  <Skeleton className='h-4 w-full' />
                  <Skeleton className='h-4 w-full' />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className='max-w-5xl rounded-lg bg-destructive/10 p-4 text-destructive'>
            <p className='font-medium'>Failed to load Privacy Policy</p>
            <p className='text-sm'>Please try refreshing the page.</p>
          </div>
        ) : document ? (
          <div className='max-w-5xl'>
            <p className='mb-6 text-muted-foreground'>
              <strong className='text-foreground'>Last updated:</strong> {new Date(document.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <div
              className='prose prose-lg prose-slate dark:prose-invert max-w-none
                prose-headings:font-semibold prose-headings:text-foreground
                prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-4
                prose-h2:text-2xl prose-h2:mt-6 prose-h2:mb-3
                prose-h3:text-xl prose-h3:mt-5 prose-h3:mb-2
                prose-p:text-foreground/90 prose-p:leading-relaxed prose-p:my-4
                prose-strong:text-foreground prose-strong:font-semibold
                prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                prose-li:my-1 prose-li:text-foreground/90
                prose-a:text-primary-blue prose-a:underline hover:prose-a:text-primary-blue/80
                prose-blockquote:border-l-4 prose-blockquote:border-primary-blue prose-blockquote:pl-4 prose-blockquote:italic
                [&_u]:underline'
              dangerouslySetInnerHTML={{ __html: document.content }}
            />
          </div>
        ) : (
          <div className='max-w-5xl rounded-lg bg-muted p-4'>
            <p className='text-muted-foreground'>Privacy Policy not available.</p>
          </div>
        )}

        <div className='mt-12 flex items-center gap-4 text-sm'>
          <Link
            href='/'
            className='text-muted-foreground hover:text-foreground'
          >
            ← Back to Home
          </Link>
          <span className='text-muted-foreground'>•</span>
          <Link
            href='/terms'
            className='text-muted-foreground hover:text-foreground'
          >
            Terms of Service
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}
